// ** Styling
import "@styles/base/plugins/charts/high-charts.scss";

import { Col, Row } from "reactstrap";
import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef } from "react";

import Card from "../ui-components/card";
import { ComponentSpinner } from "@components/spinner";
import ErrorBoundaryWrapper from "@views/common/error-boundary-wrapper";
import Flex from "@components/ui-components/flex";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { NoDataComponent } from "@components/no-data-component";
import enableExporting from "highcharts/modules/exporting";
import heatmap from "highcharts/modules/heatmap";
import highchartsAccessibility from "highcharts/modules/accessibility";
import lowerCase from "lodash/lowerCase";
import { numberCommaFormatter } from "@src/utility/helpers/utils";
import size from "lodash/size";
import startCase from "lodash/startCase";
import { toastify } from "@helpers/toast";
import { useTranslation } from "react-i18next";

enableExporting(Highcharts);

// init the module
highchartsAccessibility(Highcharts);
// import themeConfig from "@configs/theme-config";

heatmap(Highcharts); // Initialize the heatmap module
highchartsAccessibility(Highcharts);

const Charts = memo(
    forwardRef((props, parentRef) => {
        const { t } = useTranslation();
        const ref = useRef({});
        const {
            id = "",
            height = 300,
            yAxisTitle = "",
            categories = [],
            series = [],
            type = "",
            yAxis,
            xAxis,
            loading,
            cardHeader = {},
            actionItems,
            customChart = false,
            statsView,
            tooltip,
            exportTitle,
            ...otherProps
        } = props;

        const seriesName = useMemo(() => lowerCase(series[0]?.name), [series]);
        const downloadTitle = useMemo(
            () => exportTitle || cardHeader.title,
            [exportTitle, cardHeader.title]
        );

        const options = useMemo(() => {
            return {
                ...otherProps,
                accessibility: { enabled: true },
                title        : { text: "" },
                chart        : {
                    height,
                    type,
                    style: { fontFamily: "\"Fira Sans\", sans-serif" }
                },
                yAxis: yAxis || {
                    title        : { text: yAxisTitle ? t(yAxisTitle) : "" },
                    accessibility: {
                        description: yAxisTitle ? t(yAxisTitle) : ""
                    }
                },
                credits: { enabled: false },
                xAxis  : xAxis || {
                    categories,
                    crosshair: true
                },
                tooltip: size(tooltip) ? tooltip : {
                    headerFormat: "<b>{point.point.name}</b><br>",
                    shared      : true,
                    borderRadius: 5,
                    formatter () {
                        return this.points.reduce(function (
                            s,
                            point
                        ) {
                            const { name } = point.series;
                            if (
                                ["cost", "revenue"].includes(
                                    lowerCase(name)
                                ) || ["cost", "revenue"].includes(
                                    lowerCase(yAxisTitle)
                                )
                            ) return `${s}<br/>${name}: ${numberCommaFormatter(point.y)}`;
                            return `${s}<br/>${name}:  ${numberCommaFormatter(point.y)}`;
                        }, `<b>${this.x}</b>`);
                    }
                },
                legend: {
                    align        : "center",
                    verticalAlign: "bottom"
                },
                series    : series.map(item => ({ ...item, innerSize: "60%" })),
                responsive: {
                    rules: [
                        {
                            condition   : { maxWidth: 500 },
                            chartOptions: {
                                legend: {
                                    layout       : "horizontal",
                                    align        : "center",
                                    verticalAlign: "bottom"
                                }
                            }
                        }
                    ]
                },
                reversedStacks: true,
                exporting     : {
                    buttons     : { contextButton: { enabled: false } },
                    filename    : downloadTitle,
                    scale       : 1,
                    sourceWidth : 1100,
                    sourceHeight: 400,
                    chartOptions: {
                        title: {
                            text: downloadTitle ? t(
                                type === "pie" ? `${downloadTitle} - ${startCase(seriesName)}` : downloadTitle
                            ) : ""
                        },
                        plotOptions: {
                            pie: {
                                dataLabels: {
                                    formatter () {
                                        return `${this.point.name || "N/A"}: <b>${
                                            `${this.point.perc ? `${this.point.perc}%` : ""} (${
                                                this.point.y
                                            })`
                                        }</b>`;
                                    }
                                }
                            }
                        }
                    }
                },
                plotOptions: size(otherProps.plotOptions) ? otherProps.plotOptions : {
                    pie: {
                        allowPointSelect: true,
                        cursor          : "pointer",
                        showInLegend    : true,
                        dataLabels      : {
                            allowOverlap      : true,
                            crop              : false,
                            padding           : 0,
                            enabled           : true,
                            percentageDecimals: 2,
                            color             : "#002a52",
                            connectorColor    : "#002a52",
                            formatter () {
                                return `<b>${
                                    this.point.name.charAt(1) === " " ||
                                          this.point.name.charAt(1) === ":" ? this.point.name.charAt(0) : this.point.name || "N/A"
                                }</b>:${
                                    `${this.point.perc ? `${this.point.perc}%` : ""} (${
                                        this.point.y
                                    })`
                                }`;
                            },
                            style: {
                                color       : "red",
                                wordBreak   : "break-all",
                                textOverflow: "allow"
                            }
                        }
                    }
                }
            };
        }, [
            height,
            type,
            yAxisTitle,
            categories,
            series,
            tooltip,
            xAxis,
            yAxis,
            otherProps,
            downloadTitle,
            t,
            seriesName
        ]);

        const handleDownload = useCallback(async () => {
            if (ref.current) {
                const { chart } = ref.current;
                if (chart) {
                    try {
                        await chart.exportChart(
                            { type: "image/png", filename: cardHeader.title },
                            {}
                        );
                    } catch (error) {
                        toastify({
                            type : "error",
                            title: "Failed to download the chart. Please try again later."
                        });
                        console.error("Chart export error:", error);
                    }
                }
            }
        }, [cardHeader.title]);

        useImperativeHandle(parentRef, () => ({
            downloadChart () {
                handleDownload();
            }
        }));

        function hasValidSeriesData (series) {
            return (
                Array.isArray(series) &&
                series.some(
                    s => Array.isArray(s.data) &&
                        s.data.some((val) => val !== 0)
                )
            );
        }

        const ChartView = useCallback(() => {
            const isSeriesEmpty = hasValidSeriesData(series);
            return loading ? (
                <ComponentSpinner height="300px" />
            ) : isSeriesEmpty ? (
                <HighchartsReact
                    highcharts={Highcharts}
                    constructorType="chart"
                    options={options}/>
            ) : (
                <NoDataComponent title="No Data" />
            );
        }, [loading, series, options]);

        return (
            <ErrorBoundaryWrapper
                name="charts"
                children={
                    customChart ? (
                        <ChartView />
                    ) : (
                        <Card data-cy={id}>
                            <Flex
                                justify="between"
                                align="start"
                                className="w-100">
                                <Flex
                                    column
                                    className={!actionItems ? "w-100" : ""}>
                                    {cardHeader.title && (
                                        <span
                                            className="card-title"
                                            data-cy="title-header">
                                            {t(cardHeader.title)}
                                        </span>
                                    )}
                                    {cardHeader.desc && (
                                        <p
                                            className="text-muted"
                                            data-cy="desc-header">
                                            {t(cardHeader.desc)}
                                        </p>
                                    )}
                                    {cardHeader.customTitle || <></>}
                                </Flex>
                                {!loading && (
                                    <Flex
                                        className="h-100 column-gap-25 row-gap-25 w-sm-40"
                                        justify="center"
                                        align="center"
                                        data-cy="action-items">
                                        {actionItems}
                                    </Flex>
                                )}
                            </Flex>
                            <Row>
                                <Col lg={statsView ? 9 : 12}>
                                    <ChartView />
                                </Col>
                                {statsView && (
                                    <Col lg={3} className="border-start">
                                        {statsView}
                                    </Col>
                                )}
                            </Row>
                        </Card>
                    )
                }/>
        );
    })
);

export default Charts;
